import React from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { useDisplayNames } from 'hooks/useDisplayName'
import { Proposal, ProposalStatus } from '@mprolab/sdk'
import dayjs from 'dayjs'

const Card = styled.button`
  text-align: left;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const TopRow = styled(Flex)`
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
`

const StatusBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  color: ${({ theme }) => theme.colors.gray6};
`

const Meta = styled(Flex)`
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`

interface ProposalRowProps {
  proposal: Proposal
  onClick: () => void
}

function statusColors(status: ProposalStatus, colors: ReturnType<typeof useTheme>['colors']) {
  switch (status) {
    case ProposalStatus.ACTIVE:
      return { bg: `${colors.cyan}33`, color: colors.cyan }
    case ProposalStatus.PASSED:
      return { bg: `${colors.green}33`, color: colors.green }
    case ProposalStatus.EXECUTED:
      return { bg: `${colors.green}55`, color: colors.green }
    case ProposalStatus.REJECTED:
      return { bg: `${colors.red}33`, color: colors.red }
    case ProposalStatus.CANCELLED:
      return { bg: `${colors.gray8}`, color: colors.gray6 }
    default:
      return { bg: `${colors.gray8}`, color: colors.gray6 }
  }
}

const ProposalRow: React.FC<ProposalRowProps> = ({ proposal, onClick }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const statusStyle = statusColors(proposal.status, colors)
  /*
   * The proposer is a person where they chose to be one.
   *
   * The same hook the marketplace uses, and the same fallback: a private
   * profile resolves to the address the row always showed, so this is a
   * display convenience and never a dependency.
   */
  const { displayName } = useDisplayNames([proposal.proposerWallet])
  const truncatedProposer = displayName(proposal.proposerWallet)
  const closes = dayjs.unix(proposal.votingEndTimestamp)
  const isActive = proposal.status === ProposalStatus.ACTIVE

  return (
    <Card type="button" onClick={onClick}>
      <TopRow>
        <Text variant={TextVariant.BODY_DEFAULT_BOLD} isTrimmed linesToDisplay={2}>
          {proposal.title}
        </Text>
        <Flex gap="8px" alignItems="center">
          <CategoryBadge>{t(`dao.category.${proposal.category}`)}</CategoryBadge>
          <StatusBadge $bg={statusStyle.bg} $color={statusStyle.color}>
            {t(`dao.status.${proposal.status}`)}
          </StatusBadge>
        </Flex>
      </TopRow>
      <Meta>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {t('dao.proposedBy')}: {truncatedProposer}
        </Text>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {isActive
            ? t('dao.closesAt', { date: closes.format('YYYY-MM-DD HH:mm UTC') })
            : t('dao.closedAt', { date: closes.format('YYYY-MM-DD HH:mm UTC') })}
        </Text>
        {proposal.executionRecord ? (
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.green}>
            {t(`dao.executionKind.${proposal.executionRecord.executionKind}`)}
          </Text>
        ) : null}
      </Meta>
    </Card>
  )
}

export default ProposalRow
